import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import HomeRepairServiceRoundedIcon from '@mui/icons-material/HomeRepairServiceRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import Alert from '@mui/material/Alert';

import { triguardBrandProfile } from '../../branding/companyBrand';
import BrandedPreviewShell from '../previews/BrandedPreviewShell';
import { claimCXReferral, getCXReferral } from '../../services/cxReferralService';

const serviceNeeds = [
  'General pest control',
  'Termite inspection',
  'Mosquito treatment',
  'Rodent control',
];

const contactPreferences = ['Text', 'Call', 'Email'];

const ReferralLandingFormPreview: React.FC = () => {
  const brand = triguardBrandProfile;
  const { referralCode } = useParams<{ referralCode: string }>();
  const [serviceNeed, setServiceNeed] = useState(serviceNeeds[0]);
  const [contactPreference, setContactPreference] = useState(contactPreferences[0]);
  const [submitted, setSubmitted] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerContact, setCustomerContact] = useState('');
  const [referrerName, setReferrerName] = useState('Austin');
  const [error, setError] = useState('');
  const displayReferralCode = referralCode ?? 'TRIGUARD50';
  const referralBenefits = [
    'Licensed local technicians',
    'Flexible first-service scheduling',
    `${referrerName} earns rewards after your first paid service`,
  ];

  useEffect(() => {
    if (!referralCode) return;
    getCXReferral(referralCode)
      .then((referral) => setReferrerName(referral.referrerName))
      .catch(() => setError('This referral link is unavailable.'));
  }, [referralCode]);

  const handleClaim = async () => {
    if (!referralCode) {
      setSubmitted(true);
      return;
    }
    try {
      await claimCXReferral(referralCode, {
        contact: customerContact,
        contactPreference,
        name: customerName,
        serviceNeed,
      });
      setSubmitted(true);
      setError('');
    } catch {
      setError('We could not claim this offer. Check your details and try again.');
    }
  };

  return (
    <BrandedPreviewShell brand={brand} previewLabel="Referral landing" statusLabel={displayReferralCode}>
      <Stack spacing={2}>
        <Card sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack spacing={2}>
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: 'var(--cx-brand-primary-soft)',
                border: '1px solid',
                borderColor: 'var(--cx-brand-border)',
                borderRadius: 2,
                color: 'var(--cx-brand-primary-dark)',
                display: 'flex',
                gap: 1,
                px: 1.25,
                py: 1,
                width: 'fit-content',
              }}
            >
              <LocalOfferRoundedIcon sx={{ fontSize: 19 }} />
              <Typography variant="caption" sx={{ fontWeight: 900 }}>
                $50 off your first service
              </Typography>
            </Box>

            <Stack spacing={0.75}>
              <Typography
                variant="h4"
                sx={{
                  color: 'var(--cx-brand-text)',
                  fontSize: { xs: 28, sm: 32 },
                  lineHeight: 1.15,
                }}
              >
                {referrerName} invited you to {brand.companyName}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--cx-brand-text-muted)', lineHeight: 1.6 }}>
                Request service and your referral credit will be applied when the appointment is booked.
              </Typography>
            </Stack>

            <Box
              sx={{
                bgcolor: 'var(--color-gray-50)',
                border: '1px solid',
                borderColor: 'var(--cx-brand-border)',
                borderRadius: 2,
                display: 'grid',
                gap: 1,
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                p: 1,
              }}
            >
              {[
                { label: 'Credit', value: '$50' },
                { label: 'Code', value: displayReferralCode },
                { label: 'Route', value: `/r/${displayReferralCode}` },
              ].map((item) => (
                <Box key={item.label} sx={{ minWidth: 0, textAlign: 'center' }}>
                  <Typography
                    noWrap
                    sx={{
                      color: 'var(--cx-brand-text)',
                      fontSize: { xs: 15, sm: 16 },
                      fontWeight: 900,
                    }}
                  >
                    {item.value}
                  </Typography>
                  <Typography
                    noWrap
                    variant="caption"
                    sx={{ color: 'var(--cx-brand-text-muted)', fontSize: 11, fontWeight: 700 }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>
        </Card>

        <Card sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack spacing={2}>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
              <PersonAddAltRoundedIcon sx={{ color: 'var(--cx-brand-primary)', fontSize: 22 }} />
              <Typography variant="subtitle2">Request service</Typography>
            </Box>

            <Stack spacing={1.25}>
              <TextField fullWidth label="Full name" onChange={(event) => setCustomerName(event.target.value)} placeholder="Enter your name" value={customerName} />
              <TextField fullWidth label="Mobile phone" onChange={(event) => setCustomerContact(event.target.value)} placeholder="(555) 555-0142" value={customerContact} />
              <TextField fullWidth label="Email" placeholder="name@example.com" type="email" />
              <TextField fullWidth label="Service address" placeholder="Street, city, state" />
              <TextField
                fullWidth
                label="Service needed"
                onChange={(event) => setServiceNeed(event.target.value)}
                select
                value={serviceNeed}
              >
                {serviceNeeds.map((need) => (
                  <MenuItem key={need} value={need}>
                    {need}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack spacing={1}>
              <Typography variant="subtitle2">Preferred contact</Typography>
              <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                {contactPreferences.map((preference) => {
                  const selected = contactPreference === preference;

                  return (
                    <Button
                      aria-pressed={selected}
                      key={preference}
                      onClick={() => setContactPreference(preference)}
                      sx={{
                        bgcolor: selected ? 'var(--cx-brand-primary-soft)' : 'var(--color-gray-100)',
                        border: '1px solid',
                        borderColor: selected ? 'var(--cx-brand-primary)' : 'transparent',
                        color: selected ? 'var(--cx-brand-primary-dark)' : 'var(--cx-brand-text)',
                        fontWeight: 900,
                        '&:hover': {
                          bgcolor: selected ? 'var(--cx-brand-primary-soft)' : 'var(--color-gray-200)',
                          borderColor: 'var(--cx-brand-primary)',
                        },
                      }}
                      type="button"
                      variant="outlined"
                    >
                      {preference}
                    </Button>
                  );
                })}
              </Box>
            </Stack>

            <TextField
              fullWidth
              label="Notes"
              multiline
              minRows={3}
              placeholder="Tell us what is going on or when you prefer service."
            />

            <Button
              endIcon={<ArrowForwardRoundedIcon />}
              fullWidth
              disabled={Boolean(referralCode) && (!customerName.trim() || !customerContact.trim())}
              onClick={handleClaim}
              sx={{
                bgcolor: 'var(--cx-brand-primary)',
                color: 'var(--color-base-white)',
                '&:hover': { bgcolor: 'var(--cx-brand-primary-dark)' },
              }}
              type="button"
              variant="contained"
            >
              Claim $50 off
            </Button>

            {submitted ? (
              <Alert severity="success">
                You're all set. {brand.companyName} will follow up by {contactPreference.toLowerCase()}.
              </Alert>
            ) : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </Card>

        <Card sx={{ p: 2 }}>
          <Stack divider={<Divider flexItem />} spacing={1.5}>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
              <HomeRepairServiceRoundedIcon sx={{ color: 'var(--cx-brand-primary)', fontSize: 21 }} />
              <Typography variant="subtitle2">What happens next</Typography>
            </Box>

            <Stack spacing={1}>
              {referralBenefits.map((benefit) => (
                <Box key={benefit} sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                  <CheckCircleRoundedIcon sx={{ color: 'var(--cx-brand-primary)', fontSize: 19 }} />
                  <Typography variant="body2" sx={{ color: 'var(--cx-brand-text)' }}>
                    {benefit}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Card>

        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
            justifyContent: 'center',
            pb: 1,
          }}
        >
          <ShieldRoundedIcon sx={{ color: 'var(--cx-brand-text-muted)', fontSize: 17 }} />
          <Typography variant="caption" sx={{ color: 'var(--cx-brand-text-muted)' }}>
            Referral tracked for {referrerName} through {brand.domain}
          </Typography>
        </Box>
      </Stack>
    </BrandedPreviewShell>
  );
};

export default ReferralLandingFormPreview;
